import { useState } from 'react'
import { Plus, X, Globe, Check, ChevronDown, ChevronUp } from 'lucide-react'

interface Language {
  code: string
  name: string
  flag: string
}

const AVAILABLE_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
]

interface TranslationData {
  name: string
  description?: string
}

interface TranslationEditorProps {
  type: 'product' | 'category'
  translations: Record<string, TranslationData>
  onChange: (translations: Record<string, TranslationData>) => void
  supportedLanguages?: string[]
}

export default function TranslationEditor({
  type,
  translations,
  onChange,
  supportedLanguages = ['en', 'fr', 'de', 'es'],
}: TranslationEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string | null>(null)

  // Filter languages to show only supported ones
  const availableLanguages = AVAILABLE_LANGUAGES.filter(lang =>
    supportedLanguages.includes(lang.code)
  )

  // Get languages that have translations
  const translatedLanguages = availableLanguages.filter(lang =>
    translations && translations[lang.code]
  )

  // Get languages without translations
  const untranslatedLanguages = availableLanguages.filter(
    lang => !translations || !translations[lang.code]
  )

  const handleAddTranslation = (langCode: string) => {
    const newTranslations = {
      ...translations,
      [langCode]: {
        name: '',
        description: type === 'product' ? '' : undefined,
      },
    }
    onChange(newTranslations)
    setSelectedLanguage(langCode)
  }

  const handleUpdateTranslation = (langCode: string, field: 'name' | 'description', value: string) => {
    const newTranslations = {
      ...translations,
      [langCode]: {
        ...translations[langCode],
        [field]: value,
      },
    }
    onChange(newTranslations)
  }

  const handleRemoveTranslation = (langCode: string) => {
    const newTranslations = { ...translations }
    delete newTranslations[langCode]
    onChange(newTranslations)
    if (selectedLanguage === langCode) {
      setSelectedLanguage(null)
    }
  }

  const getLanguage = (code: string) => availableLanguages.find(l => l.code === code)

  return (
    <div className="border-2 border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden transition-colors">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 hover:from-blue-100 hover:to-indigo-100 dark:hover:from-blue-800/40 dark:hover:to-indigo-800/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Globe size={20} className="text-blue-600 dark:text-blue-400" />
          <span className="font-semibold text-gray-900 dark:text-white">Traduzioni</span>
          {translatedLanguages.length > 0 && (
            <span className="bg-blue-600 dark:bg-blue-500 text-white px-2 py-0.5 rounded-full text-xs font-bold">
              {translatedLanguages.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp size={20} className="text-gray-600 dark:text-slate-300" />
        ) : (
          <ChevronDown size={20} className="text-gray-600 dark:text-slate-300" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 bg-white dark:bg-slate-800/50">
          {/* Language Pills */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {translatedLanguages.map(lang => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() =>
                    setSelectedLanguage(selectedLanguage === lang.code ? null : lang.code)
                  }
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
                    selectedLanguage === lang.code
                      ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md dark:shadow-blue-900/50'
                      : 'border-green-500 dark:border-green-600 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-800/40'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="font-medium text-gray-900 dark:text-white">{lang.name}</span>
                  <Check size={16} className="text-green-600 dark:text-green-400" />
                </button>
              ))}

              {untranslatedLanguages.map(lang => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleAddTranslation(lang.code)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 hover:border-gray-300 dark:hover:border-slate-500 transition-all"
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span className="font-medium text-gray-600 dark:text-slate-300">{lang.name}</span>
                  <Plus size={16} className="text-gray-400 dark:text-slate-400" />
                </button>
              ))}
            </div>
          </div>

          {/* Translation Form */}
          {selectedLanguage && translations[selectedLanguage] && (
            <div className="border-2 border-blue-200 dark:border-blue-700 rounded-xl p-4 bg-blue-50/50 dark:bg-blue-900/20 animate-fadeIn">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getLanguage(selectedLanguage)?.flag}</span>
                  <h4 className="font-bold text-gray-900 dark:text-white">
                    Traduzione in {getLanguage(selectedLanguage)?.name}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveTranslation(selectedLanguage)}
                  className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 p-2 rounded-lg transition-colors"
                  title="Rimuovi traduzione"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Name Field */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                  Nome {type === 'product' ? 'Prodotto' : 'Categoria'}
                </label>
                <input
                  type="text"
                  value={translations[selectedLanguage].name || ''}
                  onChange={e =>
                    handleUpdateTranslation(selectedLanguage, 'name', e.target.value)
                  }
                  placeholder={`Nome in ${getLanguage(selectedLanguage)?.name}...`}
                  className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-xl focus:border-blue-600 dark:focus:border-blue-500 focus:outline-none transition-colors"
                  required
                />
              </div>

              {/* Description Field (only for products) */}
              {type === 'product' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
                    Descrizione
                  </label>
                  <textarea
                    value={translations[selectedLanguage].description || ''}
                    onChange={e =>
                      handleUpdateTranslation(selectedLanguage, 'description', e.target.value)
                    }
                    placeholder={`Descrizione in ${getLanguage(selectedLanguage)?.name}...`}
                    className="w-full px-4 py-3 bg-white dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 rounded-xl focus:border-blue-600 dark:focus:border-blue-500 focus:outline-none transition-colors resize-none"
                    rows={3}
                  />
                </div>
              )}

              {/* Helper Text */}
              <div className="mt-3 text-xs text-gray-600 dark:text-slate-300 bg-white dark:bg-slate-700/50 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600">
                💡 <strong>Suggerimento:</strong> Mantieni le traduzioni coerenti e concise.
                Evita idiomi che potrebbero non tradursi bene.
              </div>
            </div>
          )}

          {/* Empty State */}
          {translatedLanguages.length === 0 && (
            <div className="text-center py-8">
              <Globe size={48} className="mx-auto text-gray-300 dark:text-slate-600 mb-3" />
              <p className="text-gray-600 dark:text-slate-300 font-medium">Nessuna traduzione aggiunta</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                Clicca su una lingua per iniziare a tradurre
              </p>
            </div>
          )}

          {/* Info Message */}
          {translatedLanguages.length > 0 && !selectedLanguage && (
            <div className="text-center py-6 bg-gray-50 dark:bg-slate-700/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-slate-600">
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Clicca su una lingua per modificare la traduzione
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
